import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/joy';
import { tagService } from '../services/tagService';
import { Tag } from '../types/tag';
import { TagChip } from './TagChip';

interface TagSelectorProps {
  selectedTags?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
  multiple?: boolean;
  loading?: boolean;
  error?: string;
  category?: string;
  placeholder?: string;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags = [],
  onTagsChange,
  multiple = true,
  loading = false,
  error,
  category,
  placeholder = 'Select tags...',
  ...props
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadTags = async () => {
      setIsLoading(true);
      try {
        const filters: any = { is_active: true };
        if (category) filters.category = category;
        if (searchTerm) filters.search = searchTerm;
        
        const loadedTags = await tagService.getAllTags(filters);
        setTags(loadedTags);
      } catch (error) {
        console.error('Error loading tags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTags();
  }, [category, searchTerm]);

  const handleChange = (event: React.SyntheticEvent, newValue: Tag | Tag[] | null) => {
    if (onTagsChange) {
      if (Array.isArray(newValue)) {
        onTagsChange(newValue);
      } else if (newValue) {
        onTagsChange([newValue]);
      } else {
        onTagsChange([]);
      }
    }
  };

  const renderOption = (props: any, option: Tag) => (
    <Box component="li" {...props}>
      <TagChip tag={option} size="sm" />
    </Box>
  );

  const renderTags = (value: Tag[], getTagProps: any) =>
    value.map((tag, index) => (
      <TagChip
        key={tag.id}
        tag={tag}
        size="sm"
        {...getTagProps({ index })}
      />
    ));

  return (
    <Box>
      <Autocomplete
        multiple={multiple}
        options={tags}
        value={selectedTags}
        onChange={handleChange}
        onInputChange={(event, newInputValue) => setSearchTerm(newInputValue)}
        getOptionLabel={(option: Tag) => option.name}
        isOptionEqualToValue={(option: Tag, value: Tag) => option.id === value.id}
        renderOption={renderOption}
        renderTags={renderTags}
        loading={isLoading || loading}
        placeholder={placeholder}
        noOptionsText={isLoading ? 'Loading...' : 'No tags found'}
        {...props}
      />
      {error && (
        <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}; 